import { useEffect, useState } from 'react';

import type { TagEntity } from '@schibsted/frapi-schema';
import type { ByfunnFrapiSearchResponse } from '../../types/frapi-types.js';

import { generateSearchQuery } from '../components/search/helpers.js';
import { useDebounce } from '../../../core/react/hooks/index.js';

export function useSearch({
  section = undefined,
  activeFilters = [],
  presetTerm = '',
  size = 20,
  page = 1,
  callback,
}: {
  section: string | undefined;
  activeFilters: TagEntity[];
  presetTerm: string | undefined;
  size?: number;
  page?: number;
  callback?: (searchTerm: string) => void;
}) {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResult, setSearchResult] =
    useState<ByfunnFrapiSearchResponse | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (presetTerm) {
      setSearchTerm(presetTerm);
    }
  }, [presetTerm]);

  const debouncedSearchTerm = useDebounce(searchTerm, 300);

  const sanitizeSearchQuery = (str: string) =>
    str.replace(/[@#$^&%*()=[\]\\/{}:<>?.]+/gi, '');

  useEffect(() => {
    (async () => {
      try {
        if (debouncedSearchTerm.length > 0) {
          const t = sanitizeSearchQuery(debouncedSearchTerm);

          const extraParams = [
            section ? `&section=${section}` : '',
            activeFilters.length > 0
              ? generateSearchQuery(activeFilters, section)
              : '',
          ].join('');

          const raw = await fetch(
            `/api/sok?phrase=${t}&size=${size}&page=${page}${extraParams}`,
          );
          const res = await raw.json();
          setSearchResult(res);
        }
      } catch (e) {
        setError(!!e);
      }

      if (callback) {
        callback(searchTerm);
      }
    })();
  }, [debouncedSearchTerm]);

  return {
    searchTerm,
    searchResult,
    setSearchTerm,
    error,
    debouncedSearchTerm,
  };
}
